import { RootState } from "@store/index";
import Table from "@components/table";
import AccessDenied from "@components/access-denied";
import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Product, Store, Category } from "@apis/index";
import { deletesvg, edit, eye, switchOff, switchOn } from "@assets/icons";
import { useNavigate } from "react-router-dom";
import Svg from "@components/svg";
import { useModal } from "@context/ModalContext";
import DeleteModal from "@components/delete-modal";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import useConfig from "@hooks/useConfig";
import { Store as StoreIcon, Tag, X } from "lucide-react";
import { useQueryState } from "@hooks/useQueryState";

const ITEMS_PER_PAGE = 30;

const ProductListTable = ({ moduleId }: { moduleId: any }) => {
  const { settings } = useGeneralSettings();
  const { data: user } = useSelector((state: RootState) => state.user);

  const { bucketUrl } = useConfig();
  const navigate = useNavigate();
  const {
    items: products,
    totalItems,
    totalPages,
  } = useSelector((state: RootState) => state.products);
  const [page, setPage] = useQueryState<number>("page", 1);
  const [search, setSearch] = useState("");
  const { showModal } = useModal();

  // Filter states
  const [selectedStoreId, setSelectedStoreId] = useState<string>("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [stores, setStores] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [storeSearch, setStoreSearch] = useState("");
  const [isStoreDropdownOpen, setIsStoreDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const storeDropdownRef = React.useRef<HTMLDivElement>(null);
  const categoryDropdownRef = React.useRef<HTMLDivElement>(null);

  // Fetch stores and categories for filter dropdowns
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const storeRes: any = await Store.list({
          params: { limit: 1000, moduleId, minimal: true },
        });
        if (storeRes?.items) setStores(storeRes.items);
        else if (Array.isArray(storeRes)) setStores(storeRes);
      } catch (e) {}
      try {
        const catRes: any = await Category.list({
          params: { limit: 1000, moduleId },
        });
        if (catRes?.items) setCategories(catRes.items);
        else if (Array.isArray(catRes)) setCategories(catRes);
      } catch (e) {}
    };
    fetchFilters();
  }, [moduleId]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        storeDropdownRef.current &&
        !storeDropdownRef.current.contains(e.target as Node)
      ) {
        setIsStoreDropdownOpen(false);
      }
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(e.target as Node)
      ) {
        setIsCategoryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const [error, setError] = useState<string | null>(null);

  const fetchProducts = async (p = page) => {
    setError(null);
    const params: any = {
      page: p,
      limit: ITEMS_PER_PAGE,
      moduleId,
      search,
    };
    if (selectedStoreId) params.storeId = selectedStoreId;
    if (selectedCategoryId) params.categoryId = selectedCategoryId;
    try {
      await Product.list({
        state: "products",
        params,
      });
    } catch (err: any) {
      console.error("Failed to fetch products:", err);
      setError(err.message || "Failed to load products");
    }
  };

  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (page === 1) {
      fetchProducts(1);
    } else {
      setPage(1);
    }
  }, [moduleId, search, selectedStoreId, selectedCategoryId]);

  useEffect(() => {
    fetchProducts(page);
  }, [page]);

  const getRandomColor = (name: string) => {
    const colors = [
      "#F56565",
      "#ED8936",
      "#ECC94B",
      "#48BB78",
      "#38B2AC",
      "#4299E1",
      "#667EEA",
      "#9F7AEA",
      "#ED64A6",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getInitials = (name: string) => {
    return name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2)
      : "?";
  };

  const columns: any = [
    {
      title: "SN",
      key: "sn",
      render: (_, i) => i + (page - 1) * ITEMS_PER_PAGE,
    },
    {
      title: "Product",
      key: "product",
      render: (product: any) => {
        const imageUrl = product.image
          ? `${bucketUrl}/${product.image}`
          : product.images?.[0]?.image
            ? `${bucketUrl}/${product.images[0].image}`
            : null;

        return (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                justifyContent: "flex-start",
              }}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={product.name}
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "8px",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "8px",
                    backgroundColor: getRandomColor(product.name || "Product"),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: "16px",
                    flexShrink: 0,
                  }}
                >
                  {getInitials(product.name || "Product")}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "4px",
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#333",
                    lineHeight: "1.2",
                  }}
                >
                  {product.name}
                </span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                  {product.category?.name && (
                    <span
                      style={{
                        fontSize: "10px",
                        color: "#C05621",
                        background: "#FEEBC8",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontWeight: 600,
                      }}
                    >
                      {product.category.name}
                    </span>
                  )}
                  {product.subCategories?.map((s: any, idx: number) => (
                    <span
                      key={idx}
                      style={{
                        fontSize: "10px",
                        color: "#555",
                        background: "#f0f0f0",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      {s.name}
                    </span>
                  ))}
                  {product.brand?.name && (
                    <span
                      style={{
                        fontSize: "10px",
                        color: "#2B6CB0",
                        background: "#BEE3F8",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontWeight: 600,
                      }}
                    >
                      {product.brand.name}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Store",
      key: "store",
      render: (product: any) => {
        const storeName =
          product.store?.name || product.store?.storeName || "Store";
        const storeImage =
          product.store?.avatar || product.store?.logo
            ? `${bucketUrl}/${product.store?.avatar || product.store?.logo}`
            : null;

        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (product.store?.id) {
                navigate(
                  `/admin/users/store/store-lists/view/${product.store.userId}`,
                );
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              cursor: "pointer",
            }}
          >
            {storeImage ? (
              <img
                src={storeImage}
                alt={storeName}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "4px", // Made square (slight radius)
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "4px", // Made square (slight radius)
                  backgroundColor: getRandomColor(storeName),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "14px",
                  flexShrink: 0,
                }}
              >
                {getInitials(storeName)}
              </div>
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <span
                style={{ fontWeight: 500, fontSize: "14px", color: "#333" }}
              >
                {storeName}
              </span>
              <span style={{ fontSize: "12px", color: "#777" }}>
                {product.store?.phoneNo || product.store?.storeNumber || "-"}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Stock",
      key: "stock",
      render: (product: any) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {product.stockInfos ? (
            <div
              style={{
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                backgroundColor:
                  product.stockInfos.type === "unlimited"
                    ? "#e8f5e9" // Light green for unlimited
                    : product.stockInfos.stock !== undefined &&
                        product.stockInfos.stock !== null
                      ? product.stockInfos.stock > 10
                        ? "#e8f5e9" // Green for high stock
                        : product.stockInfos.stock > 0
                          ? "#fff3e0" // Orange for low stock
                          : "#ffebee" // Red for out of stock
                      : "#f5f5f5", // Gray for N/A
                color:
                  product.stockInfos.type === "unlimited"
                    ? "#2e7d32" // Dark green text
                    : product.stockInfos.stock !== undefined &&
                        product.stockInfos.stock !== null
                      ? product.stockInfos.stock > 10
                        ? "#2e7d32" // Dark green text
                        : product.stockInfos.stock > 0
                          ? "#e65100" // Dark orange text
                          : "#c62828" // Dark red text
                      : "#757575", // Gray text for N/A
                border: "1px solid",
                borderColor:
                  product.stockInfos.type === "unlimited"
                    ? "#c8e6c9" // Light green border
                    : product.stockInfos.stock !== undefined &&
                        product.stockInfos.stock !== null
                      ? product.stockInfos.stock > 10
                        ? "#c8e6c9" // Light green border
                        : product.stockInfos.stock > 0
                          ? "#ffe0b2" // Light orange border
                          : "#ffcdd2" // Light red border
                      : "#e0e0e0", // Light gray border
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "60px",
              }}
            >
              {product.stockInfos.type === "unlimited"
                ? "Unlimited"
                : product.stockInfos.stock !== undefined &&
                    product.stockInfos.stock !== null
                  ? `${product.stockInfos.stock}${product.stockInfos.type === "daily" ? " (Daily)" : ""}`
                  : "N/A"}
            </div>
          ) : (
            <div
              style={{
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                backgroundColor: "#f5f5f5",
                color: "#999",
                border: "1px solid #e0e0e0",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "60px",
              }}
            >
              -
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Delivery",
      key: "delivery",
      render: (product: any) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {product.deliveryAvailability ? (
            <span
              style={{
                fontSize: "12px",
                color: "#333",
              }}
            >
              {product.deliveryAvailability}
              {product.deliveryAvailability === "regular" &&
              product.estimatedDeliveryDay
                ? ` (${product.estimatedDeliveryDay}d)`
                : ""}
            </span>
          ) : (
            <span style={{ fontSize: "12px", color: "#999" }}>-</span>
          )}
        </div>
      ),
    },
    {
      title: `Price (${settings.currencySymbol})`,
      key: "price",
      render: (product: any) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <span style={{ fontWeight: 500 }}>
            {product.actualAmount || product.price}
          </span>
          {product.actualAmount && product.actualAmount !== product.price && (
            <span
              style={{
                fontSize: "11px",
                color: "#999",
                textDecoration: "line-through",
              }}
            >
              {product.price}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "is Active",
      key: "discountType",
      render: (item) => (
        <Svg
          src={item.isActive ? switchOn : switchOff}
          onClick={async () => {
            const formData = new FormData();
            const payload = item.isActive ? "false" : "true";
            formData.append("isActive", payload);
            Product.update({
              state: "products",
              id: item.id,
              data: formData,
            }).then(() => {
              fetchProducts();
            });
          }}
        />
      ),
    },
    {
      title: "Most Popular",
      key: "mostPopular",
      render: (item) => (
        <Svg
          src={item.isPopular ? switchOn : switchOff}
          onClick={async () => {
            const formData = new FormData();
            const payload = item.isPopular ? "false" : "true";
            formData.append("isPopular", payload);
            Product.update({
              state: "products",
              id: item.id,
              data: formData,
            }).then(() => {
              fetchProducts();
            });
          }}
        />
      ),
    },
    {
      title: "is Most Ordered",
      key: "discountType",
      render: (item) => (
        <Svg
          src={item.isMostOrdered ? switchOn : switchOff}
          onClick={async () => {
            const formData = new FormData();
            const payload = item.isMostOrdered ? "false" : "true";
            formData.append("isMostOrdered", payload);
            Product.update({
              state: "products",
              id: item.id,
              data: formData,
            }).then(() => {
              fetchProducts();
            });
          }}
        />
      ),
    },
    {
      title: "is Latest Arrival",
      key: "discountType",
      render: (item) => (
        <Svg
          src={item.isLatestArrival ? switchOn : switchOff}
          onClick={async () => {
            const formData = new FormData();
            const payload = item.isLatestArrival ? "false" : "true";
            formData.append("isLatestArrival", payload);
            Product.update({
              state: "products",
              id: item.id,
              data: formData,
            }).then(() => {
              fetchProducts();
            });
          }}
        />
      ),
    },
  ];

  const selectedStore = stores.find(
    (s: any) => String(s.userId || s.id) === selectedStoreId,
  );
  const selectedCategory = categories.find(
    (c: any) => String(c.id) === selectedCategoryId,
  );

  const filteredStores = stores.filter((s: any) =>
    (s.storeName || s.name || "")
      .toLowerCase()
      .includes(storeSearch.toLowerCase()),
  );
  const filteredCategories = categories.filter((c: any) =>
    (c.name || "").toLowerCase().includes(categorySearch.toLowerCase()),
  );

  const hasActiveFilters = !!selectedStoreId || !!selectedCategoryId;

  if (error) {
    return (
      <AccessDenied
        message={error}
        onRetry={() => fetchProducts(page)}
      />
    );
  }

  return (
    <div>
      {/* Filter Bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "12px 16px",
          marginBottom: "0",
          flexWrap: "wrap",
        }}
      >
        {/* Store Filter */}
        <div
          ref={storeDropdownRef}
          style={{ position: "relative", minWidth: "200px" }}
        >
          <div
            onClick={() => {
              setIsStoreDropdownOpen(!isStoreDropdownOpen);
              setIsCategoryDropdownOpen(false);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              border: selectedStoreId
                ? "1.5px solid #E53E3E"
                : "1px solid #E2E8F0",
              borderRadius: "8px",
              cursor: "pointer",
              background: selectedStoreId ? "#FFF5F5" : "#fff",
              fontSize: "13px",
              color: selectedStoreId ? "#C53030" : "#4A5568",
              fontWeight: selectedStoreId ? 600 : 400,
              transition: "all 0.2s ease",
              minHeight: "36px",
            }}
          >
            <StoreIcon size={15} style={{ flexShrink: 0, opacity: 0.7 }} />
            <span
              style={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedStore
                ? selectedStore.storeName || selectedStore.name || "Store"
                : "All Stores"}
            </span>
            {selectedStoreId ? (
              <X
                size={14}
                style={{ flexShrink: 0, cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedStoreId("");
                  setStoreSearch("");
                }}
              />
            ) : (
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                style={{ flexShrink: 0, opacity: 0.5 }}
              >
                <path
                  d="M3 5L6 8L9 5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </div>
          {isStoreDropdownOpen && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                right: 0,
                background: "#fff",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                zIndex: 100,
                maxHeight: "280px",
                display: "flex",
                flexDirection: "column",
                minWidth: "240px",
              }}
            >
              <div style={{ padding: "8px" }}>
                <input
                  type="text"
                  placeholder="Search stores..."
                  value={storeSearch}
                  onChange={(e) => setStoreSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  style={{
                    width: "100%",
                    padding: "7px 10px",
                    border: "1px solid #E2E8F0",
                    borderRadius: "6px",
                    fontSize: "13px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div style={{ overflowY: "auto", maxHeight: "220px" }}>
                <div
                  onClick={() => {
                    setSelectedStoreId("");
                    setIsStoreDropdownOpen(false);
                    setStoreSearch("");
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: "13px",
                    color: !selectedStoreId ? "#E53E3E" : "#4A5568",
                    fontWeight: !selectedStoreId ? 600 : 400,
                    background: !selectedStoreId ? "#FFF5F5" : "transparent",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedStoreId)
                      (e.target as HTMLElement).style.background = "#F7FAFC";
                  }}
                  onMouseLeave={(e) => {
                    if (selectedStoreId)
                      (e.target as HTMLElement).style.background =
                        "transparent";
                  }}
                >
                  All Stores
                </div>
                {filteredStores.map((store: any) => {
                  const storeVal = String(store.userId || store.id);
                  const isActive = selectedStoreId === storeVal;
                  return (
                    <div
                      key={storeVal}
                      onClick={() => {
                        setSelectedStoreId(storeVal);
                        setIsStoreDropdownOpen(false);
                        setStoreSearch("");
                      }}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        fontSize: "13px",
                        color: isActive ? "#E53E3E" : "#4A5568",
                        fontWeight: isActive ? 600 : 400,
                        background: isActive ? "#FFF5F5" : "transparent",
                        transition: "background 0.15s",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive)
                          (e.currentTarget as HTMLElement).style.background =
                            "#F7FAFC";
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive)
                          (e.currentTarget as HTMLElement).style.background =
                            "transparent";
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {store.storeName || store.name || "Unnamed Store"}
                      </span>
                    </div>
                  );
                })}
                {filteredStores.length === 0 && (
                  <div
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      color: "#A0AEC0",
                      fontSize: "13px",
                    }}
                  >
                    No stores found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Category Filter */}
        <div
          ref={categoryDropdownRef}
          style={{ position: "relative", minWidth: "200px" }}
        >
          <div
            onClick={() => {
              setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
              setIsStoreDropdownOpen(false);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              border: selectedCategoryId
                ? "1.5px solid #DD6B20"
                : "1px solid #E2E8F0",
              borderRadius: "8px",
              cursor: "pointer",
              background: selectedCategoryId ? "#FFFAF0" : "#fff",
              fontSize: "13px",
              color: selectedCategoryId ? "#C05621" : "#4A5568",
              fontWeight: selectedCategoryId ? 600 : 400,
              transition: "all 0.2s ease",
              minHeight: "36px",
            }}
          >
            <Tag size={15} style={{ flexShrink: 0, opacity: 0.7 }} />
            <span
              style={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedCategory ? selectedCategory.name : "All Categories"}
            </span>
            {selectedCategoryId ? (
              <X
                size={14}
                style={{ flexShrink: 0, cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCategoryId("");
                  setCategorySearch("");
                }}
              />
            ) : (
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                style={{ flexShrink: 0, opacity: 0.5 }}
              >
                <path
                  d="M3 5L6 8L9 5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </div>
          {isCategoryDropdownOpen && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                right: 0,
                background: "#fff",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                zIndex: 100,
                maxHeight: "280px",
                display: "flex",
                flexDirection: "column",
                minWidth: "240px",
              }}
            >
              <div style={{ padding: "8px" }}>
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  style={{
                    width: "100%",
                    padding: "7px 10px",
                    border: "1px solid #E2E8F0",
                    borderRadius: "6px",
                    fontSize: "13px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div style={{ overflowY: "auto", maxHeight: "220px" }}>
                <div
                  onClick={() => {
                    setSelectedCategoryId("");
                    setIsCategoryDropdownOpen(false);
                    setCategorySearch("");
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: "13px",
                    color: !selectedCategoryId ? "#DD6B20" : "#4A5568",
                    fontWeight: !selectedCategoryId ? 600 : 400,
                    background: !selectedCategoryId ? "#FFFAF0" : "transparent",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedCategoryId)
                      (e.target as HTMLElement).style.background = "#F7FAFC";
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCategoryId)
                      (e.target as HTMLElement).style.background =
                        "transparent";
                  }}
                >
                  All Categories
                </div>
                {filteredCategories.map((cat: any) => {
                  const isActive = selectedCategoryId === String(cat.id);
                  return (
                    <div
                      key={cat.id}
                      onClick={() => {
                        setSelectedCategoryId(String(cat.id));
                        setIsCategoryDropdownOpen(false);
                        setCategorySearch("");
                      }}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        fontSize: "13px",
                        color: isActive ? "#DD6B20" : "#4A5568",
                        fontWeight: isActive ? 600 : 400,
                        background: isActive ? "#FFFAF0" : "transparent",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive)
                          (e.currentTarget as HTMLElement).style.background =
                            "#F7FAFC";
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive)
                          (e.currentTarget as HTMLElement).style.background =
                            "transparent";
                      }}
                    >
                      {cat.name}
                    </div>
                  );
                })}
                {filteredCategories.length === 0 && (
                  <div
                    style={{
                      padding: "12px",
                      textAlign: "center",
                      color: "#A0AEC0",
                      fontSize: "13px",
                    }}
                  >
                    No categories found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Clear All Filters */}
        {hasActiveFilters && (
          <button
            onClick={() => {
              setSelectedStoreId("");
              setSelectedCategoryId("");
              setStoreSearch("");
              setCategorySearch("");
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "7px 12px",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              background: "#fff",
              cursor: "pointer",
              fontSize: "12px",
              color: "#718096",
              fontWeight: 500,
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = "#FFF5F5";
              (e.currentTarget as HTMLElement).style.color = "#E53E3E";
              (e.currentTarget as HTMLElement).style.borderColor = "#FEB2B2";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = "#fff";
              (e.currentTarget as HTMLElement).style.color = "#718096";
              (e.currentTarget as HTMLElement).style.borderColor = "#E2E8F0";
            }}
          >
            <X size={13} />
            Clear Filters
          </button>
        )}
      </div>

      <Table
        text="Product List"
        columns={columns}
        data={products || []}
        totalItems={totalItems}
        totalPages={totalPages}
        pageControlled={page}
        onPageChange={setPage}
        itemsPerPage={ITEMS_PER_PAGE}
        hasSearchbar={true}
        onSearchChange={setSearch}
        actions={[
          {
            key: "view",
            icon: (
              <Svg
                src={eye}
                styleSvg={`
                  width: 20px; 
                  height: 20px; 
                  svg { 
                    color: #48BB78 !important; 
                    fill: none !important; 
                    stroke: #48BB78 !important; 
                  }
                `}
              />
            ),
            onClick: (item) => {
              navigate(`/admin/users/store/update-product/${item.id}`, {
                state: { productData: { ...item, moduleId } },
              });
            },
          },
          {
            key: "edit",
            icon: (
              <Svg
                src={edit}
                styleSvg={`
                  width: 20px; 
                  height: 20px; 
                  svg { 
                    color: #4299E1 !important; 
                    fill: none !important; 
                    stroke: #4299E1 !important; 
                  }
                `}
              />
            ),
            onClick: (item) => {
              navigate(`/admin/users/store/update-product/${item.id}`, {
                state: { productData: { ...item, moduleId } },
              });
            },
          },
          {
            key: "delete",
            icon: (
              <Svg
                src={deletesvg}
                styleSvg={`
                  width: 20px; 
                  height: 20px; 
                  svg { 
                    color: #F56565 !important; 
                    fill: none !important; 
                    stroke: #F56565 !important; 
                  }
                `}
              />
            ),
            onClick: async (item) => {
              showModal(
                <DeleteModal
                  state="products"
                  api={Product}
                  id={item.id}
                  onSuccess={() => fetchProducts()}
                />,
              );
            },
          },
        ]}
      />
    </div>
  );
};

export default ProductListTable;
